import React from "react";
import * as ReactDOMServer from "react-dom/server";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";

import variableColors from "../../_App.module.scss";
import { dynamicTextColor, getAcronym } from "../../utils/helpers";
import { configAgencyName } from "../../config";
import { LegendDiv } from "../views/SelectedBusViewMapLegend";
import { speedColorMapKmh, speedColorMapMph } from "../../utils/swiftlyHelpers";

const { darkColor } = variableColors;

//* The main component
const AverageSpeedsMapLegend = ({
  currentRouteData,
  averageSpeedsDirectionId,
  baseUnit,
  kindOfSpeed,
  setKindOfSpeed,
  availableSpeeds,
}: {
  currentRouteData: RouteDetails;
  averageSpeedsDirectionId: string;
  baseUnit: string;
  kindOfSpeed: string;
  setKindOfSpeed: React.Dispatch<React.SetStateAction<string>>;
  availableSpeeds: string[];
}): JSX.Element => {
  const { t } = useTranslation();
  const leafletMap: L.Map = useMap();

  //* Adding legend
  React.useEffect(() => {
    if (!leafletMap) return;

    if (leafletMap && baseUnit) {
      const baseSpeedUnit: string = baseUnit === "mph" ? "mph" : "km/h";
      const legendEntries: Map<number, string> = baseUnit === "mph" ? speedColorMapMph : speedColorMapKmh;
      // console.log("legendEntries:", legendEntries);

      const spanStyle = { textAlign: "center" as const, minWidth: "36px", padding: "2px 4px" };
      const legend = new L.Control({ position: "bottomright" }) as L.Control;

      legend.onAdd = (): HTMLElement => {
        const div: HTMLDivElement = L.DomUtil.create("div", "map-route-legend");

        // Create select element
        const select = L.DomUtil.create("select", "form-select form-select-sm", div);
        availableSpeeds.forEach((optionText: string) => {
          const option = document.createElement("option");
          option.text = optionText;
          option.value = optionText?.replace(" ", "_");
          if (option.value === kindOfSpeed?.replace(" ", "_")) {
            option.selected = true; // Mark this option as selected
          }
          select.add(option);
        });

        // Optional: add event listener for select change
        L.DomEvent.on(select, "change", (event: Event) => {
          const selectedValue: string = (event as unknown as React.ChangeEvent<HTMLSelectElement>).target.value;
          // console.log("selectedValue:", selectedValue);
          setKindOfSpeed(selectedValue);
        });

        const legendContent = (
          <LegendDiv $width={"auto"}>
            <p>
              {`${t("Linia")}:`}{" "}
              <span
                style={{
                  ...spanStyle,
                  color: dynamicTextColor(currentRouteData?.color),
                  backgroundColor: currentRouteData?.color,
                  border: `1px solid ${darkColor}`,
                }}
              >
                {/* //* Show Acronym if agency is Miami Beach */}
                {configAgencyName === ("MiamiBeach" as string)
                  ? getAcronym(currentRouteData?.longName)
                  : currentRouteData?.shortName || currentRouteData?.id || currentRouteData?.longName}
              </span>
            </p>
            <p>
              {`${t("Kierunek")}:`} <span style={spanStyle}>{averageSpeedsDirectionId || t("b/d")}</span>
            </p>

            {/* //* Speed Color Legend */}
            <div style={{ marginTop: "0.75rem" }}>
              <h5>{`${t("Prędkość")} [${baseSpeedUnit}]`}</h5>
              <h6>{kindOfSpeed.replace("_", " ")}</h6>
              {Array.from(legendEntries?.entries())
                ?.sort((a: [number, string], b: [number, string]) => a[0] - b[0])
                ?.map(([speed, color]: [number, string], idx: number, arr: [number, string][]) => {
                  const nextSpeed: number = arr[idx + 1]?.[0];
                  const range: string = nextSpeed !== undefined ? `${speed} - ${nextSpeed - 1}` : `${speed}+`;
                  return (
                    <div key={speed} style={{ display: "flex", alignItems: "center", marginBottom: "0.25rem" }}>
                      <i
                        style={{
                          backgroundColor: color,
                          width: "1rem",
                          height: "1rem",
                          marginRight: "0.5rem",
                          opacity: 0.7,
                          border: `1px solid ${darkColor}`,
                        }}
                      />
                      <span>{`${range} ${baseSpeedUnit}`}</span>
                    </div>
                  );
                })}

              {/* //* No Data square */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: "0.25rem" }}>
                <i
                  style={{
                    backgroundColor: darkColor,
                    width: "1rem",
                    height: "1rem",
                    marginRight: "0.5rem",
                    opacity: 0.9,
                    border: `1px solid ${darkColor}`,
                  }}
                />
                <span>{t("Brak danych")}</span>
              </div>
            </div>
          </LegendDiv>
        ) as JSX.Element;

        div.innerHTML = ReactDOMServer.renderToString(legendContent);
        // Append select to legend div
        div.appendChild(select);
        return div;
      };

      legend.addTo(leafletMap);

      return () => {
        if (legend) {
          legend.remove();
        }
      };
    }
  }, [availableSpeeds, averageSpeedsDirectionId, baseUnit, currentRouteData, kindOfSpeed, leafletMap, setKindOfSpeed, t]);

  return <React.Fragment>{null}</React.Fragment>;
};

export default AverageSpeedsMapLegend;
